import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useOrder, TransportMode } from '../context/OrderContext'
import { useAuth } from '../context/AuthContext'
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet'
import L from 'leaflet'
import Stepper from "../components/Stepper"
import OrderSummary from "../components/OrderSummary"
import {
  CRISSIER,
  geocodeAddress,
  fetchSuggestions,
  haversineDistance,
} from '../lib/locationApi'


const DeliveryPage: React.FC = () => {
  const navigate = useNavigate()
  const { draft, setDelivery, setDeliverySlot, goToStep, canGoToStep } = useOrder()
  const { profile } = useAuth()

  const [deliveryModes, setDeliveryModes] = useState<TransportMode[]>([])
  const [selectedMode, setSelectedMode] = useState<TransportMode | null>(null)
  const [deliveryAddress, setDeliveryAddress] = useState('')

  const [suggestions, setSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  const [deliveryMarker, setDeliveryMarker] = useState<{ lat: number; lng: number } | null>(null)
  const [distanceKm, setDistanceKm] = useState<number | null>(null)
  const [deliveryPrice, setDeliveryPrice] = useState<number | null>(null)

  const [weeklySlots, setWeeklySlots] = useState<any[]>([])
  const [selectedSlot, setSelectedSlot] = useState<any | null>(null)

  const [loading, setLoading] = useState(true)

  // ---------- Chargement des modes de livraison ----------
  useEffect(() => {
    if (!draft.service) {
      navigate('/order/start')
      return
    }

    const load = async () => {
      setLoading(true)

      const [{ data: modes }, { data: slots }] = await Promise.all([
        supabase.from('transport_modes').select('*').eq('type_mode', 'DELIVERY').eq('is_active', true),
        supabase.from('weekly_slots').select('*').eq('slot_type', 'DELIVERY').eq('is_active', true)
      ])

      setDeliveryModes(modes ?? [])
      setWeeklySlots(slots ?? [])
      setLoading(false)
    }

    load()
  }, [draft.service, navigate])

  // ---------- Pré-remplir l'adresse ----------
  useEffect(() => {
    if (profile?.address_line1) setDeliveryAddress(profile.address_line1)
  }, [profile])

  // ---------- Gestion des steps ----------
  useEffect(() => {
    if (!draft.pickupMode || !canGoToStep("DELIVERY")) {
      navigate("/order/pickup")
      return
    }
    goToStep("DELIVERY")
  }, [draft.pickupMode, canGoToStep, goToStep, navigate])

  const modeNeedsAddress =
    selectedMode?.pricing_mode === "PER_KM"

  // ---------- Calcul distance + position ----------
  const handleCalculateDistance = async () => {
  if (!selectedMode) {
    alert("Sélectionne un mode de livraison.")
    return
  }

  if (!deliveryAddress) {
    alert("Merci de renseigner une adresse.")
    return
  }

  const geo = await geocodeAddress(deliveryAddress)
  if (!geo) {
    alert("Adresse introuvable.")
    return
  }

  setDeliveryMarker(geo)

  const km = haversineDistance(CRISSIER.lat, CRISSIER.lng, geo.lat, geo.lng)
  setDistanceKm(km)

  // 🔢 distance max depuis le mode (fallback 50 km)
  const maxDistanceKm = selectedMode.max_distance_km ?? 50

  if (km > maxDistanceKm) {
    setDeliveryPrice(null)
    alert(`Livraison disponible uniquement dans un rayon de ${maxDistanceKm} km autour de Crissier.`)
    return
  }

  if (selectedMode.pricing_mode === "PER_KM") {
    const pricePerKm = selectedMode.price_per_km ?? 0
    const minPrice = selectedMode.min_price ?? 0
    const price = Math.max(minPrice, km * pricePerKm)
    setDeliveryPrice(price)
  } else {
    setDeliveryPrice(selectedMode.fixed_price ?? 0)
  }
}

  // ---------- Validation ----------
  const handleValidate = () => {
    if (!selectedMode) return alert("Sélectionne un mode de livraison.")

    if (modeNeedsAddress) {
      if (!deliveryAddress) return alert("Adresse obligatoire pour ce mode.")
      // 🟢 on exige un calcul fait : null = pas calculé
      if (distanceKm == null || deliveryPrice == null) {
        return alert("Merci de valider l'adresse de livraison avant de continuer.")
      }
    }

    if (selectedSlot) setDeliverySlot(selectedSlot)

    setDelivery(
      selectedMode,
      modeNeedsAddress ? deliveryAddress : null,
      modeNeedsAddress ? (distanceKm ?? 0) : 0,
      modeNeedsAddress ? (deliveryPrice ?? 0) : (selectedMode.fixed_price ?? 0)
    )

    navigate("/order/payment")
  }

  if (loading) return <p>Chargement…</p>

  return (
    <div className="card">
      <Stepper />

      <div className="card-header">
        <h1 className="card-title">Livraison</h1>
      </div>

      {/* --- LISTE MODES --- */}
      <div className="grid-2" style={{ marginTop: "1rem" }}>
        {deliveryModes.map(mode => {
          const modeIsPerKm = mode.pricing_mode === "PER_KM"
          return (
            <button
              key={mode.id}
              className={`card ${selectedMode?.id === mode.id ? "card--active" : ""}`}
              style={{
                border: selectedMode?.id === mode.id ? "2px solid #22c55e" : '1px solid var(--tun-dropdown-border)'
              }}
              onClick={() => {
                setSelectedMode(mode)

                if (modeIsPerKm) {
                  // 🟢 on force un nouveau calcul pour ce mode au km
                  setDistanceKm(null)
                  setDeliveryPrice(null)
                  setDeliveryMarker(null)
                } else {
                  // mode fixe → pas besoin de calcul distance
                  setDistanceKm(null)
                  setDeliveryPrice(mode.fixed_price ?? 0)
                  setDeliveryMarker(null)
                }
              }}
            >
              <div className="card-header" style={{ justifyContent: "space-between" }}>
                <div>
                  <div className="card-title">{mode.label}</div>
                  <div className="card-subtitle">{mode.description}</div>
                </div>
                <strong style={{ color: "#22c55e" }}>
                  {mode.pricing_mode === "FIXED"
                    ? `${mode.fixed_price} CHF`
                    : `${mode.price_per_km} CHF/km`}
                </strong>
              </div>
            </button>
          )
        })}
      </div>

      {/* --- ADRESSE + AUTOCOMPLETE --- */}
      {modeNeedsAddress && (
        <div style={{ marginTop: "1.5rem", position: "relative" }}>
          <label className="label">Adresse de livraison</label>
          <input
            className="input"
            value={deliveryAddress}
            placeholder="Rue, NPA, Ville"
            onChange={async (e) => {
              const v = e.target.value
              setDeliveryAddress(v)

              // 🟢 toute modification d'adresse pour un mode au km invalide le calcul
              setDistanceKm(null)
              setDeliveryPrice(null)

              if (v.length >= 3) {
                const list = await fetchSuggestions(v)
                setSuggestions(list)
                setShowSuggestions(true)
              } else {
                setShowSuggestions(false)
              }
            }}
            onFocus={() => suggestions.length && setShowSuggestions(true)}
          />

          {showSuggestions && suggestions.length > 0 && (
            <div
              style={{
                position: "relative",
                left: 0,
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                background: 'var(--tun-dropdown-bg)',
                border: '1px solid var(--tun-dropdown-border)',
                zIndex: 70
              }}
            >
              {suggestions.map((s, i) => (
                <div
                  key={i}
                  className="suggestion"
                  style={{ padding: ".5rem", cursor: "pointer" }}
                  onClick={() => {
                    setDeliveryAddress(s.display_name)
                    setDeliveryMarker({ lat: parseFloat(s.lat), lng: parseFloat(s.lng) })
                    // 🟢 on invalide aussi le calcul existant pour forcer un recalcul du prix
                    setDistanceKm(null)
                    setDeliveryPrice(null)
                    setShowSuggestions(false)
                  }}
                >
                  {s.display_name}
                </div>
              ))}
            </div>
          )}

          <button
            data-testid="calculate-distance-button"
            className="btn btn-secondary"
            style={{ marginTop: ".5rem" }}
            onClick={handleCalculateDistance}
          >
            Valider cette adresse (et calculer le tarif)
          </button>

          {distanceKm !== null && (
            <p style={{ marginTop: ".5rem", color: deliveryPrice != null ? "#22c55e" : "#f87171" }}>
              Distance : {distanceKm.toFixed(1)} km —{" "}
              {deliveryPrice != null ? `${deliveryPrice.toFixed(2)} CHF` : "hors zone"}
            </p>
          )}
        </div>
      )}

      {/* --- CARTE --- 
      <div style={{ marginTop: "1.5rem", height: "280px", borderRadius: "1rem", overflow: "hidden" }}>
        <MapContainer
          center={
            deliveryMarker
              ? [deliveryMarker.lat, deliveryMarker.lng]
              : [CRISSIER.lat, CRISSIER.lng]
          }
          zoom={deliveryMarker ? 11 : 10}
          scrollWheelZoom={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="© OpenStreetMap"
          />

          <Circle
            center={[CRISSIER.lat, CRISSIER.lng]}
            radius={50000}
            pathOptions={{ color: "#22c55e", fillOpacity: 0.1 }}
          />

          <Marker
            position={[CRISSIER.lat, CRISSIER.lng]}
            icon={L.icon({
              iconUrl: "https://cdn-icons-png.flaticon.com/512/535/535239.png",
              iconSize: [24, 24],
            })}
          />

          {deliveryMarker && (
            <>
              <Marker
                position={[deliveryMarker.lat, deliveryMarker.lng]}
                icon={L.icon({
                  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
                  iconSize: [28, 28],
                })}
              />
              <Circle
                center={[deliveryMarker.lat, deliveryMarker.lng]}
                radius={200}
                pathOptions={{
                  color: distanceKm && distanceKm > 50 ? "#ef4444" : "#22c55e",
                  fillOpacity: 0.3,
                }}
              />
            </>
          )}
        </MapContainer>
      </div>
      */}
      {/* --- Créneaux (optionnels) --- 
      <div style={{ marginTop: "1.5rem" }}>
        <h2 className="subtitle">Créneau (optionnel)</h2>

        {weeklySlots.length === 0 ? (
          <p style={{ color: "#9ca3af" }}>Aucun créneau disponible.</p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
            {weeklySlots.map(slot => {
              const labelDay = ["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"][slot.weekday - 1]
              return (
                <button
                  key={slot.id}
                  className={`btn ${selectedSlot?.start === slot.start_time ? "btn-primary" : "btn-ghost"}`}
                  onClick={() =>
                    setSelectedSlot({
                      date: labelDay,
                      start: slot.start_time,
                      end: slot.end_time,
                    })
                  }
                >
                  {labelDay} {slot.start_time}-{slot.end_time}
                </button>
              )
            })}
          </div>
        )}
      </div>
      */}
      {/* --- Récap --- */}
      <div style={{ marginTop: "1.5rem" }}>
        <OrderSummary />
      </div>

      {/* --- Validation --- */}
      <button
        data-testid="continue-button"
        className="btn btn-primary"
        style={{ marginTop: "1.5rem" }}
        onClick={handleValidate}
        disabled={!selectedMode}
      >
        Continuer
      </button>
    </div>
  )
}

export default DeliveryPage
